import { useState, useEffect, type FormEvent } from "react";
import { useAuth } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import * as api from "@/lib/api";
import type { UserSchoolAssociationPublic, UserSchoolEmailPublic } from "@dugads/shared";
import { FamilyCard } from "@/components/family-card";
import { PrivacyDataCard } from "@/components/privacy-data-card";

export default function ProfilePage() {
  const { user, changePassword, error } = useAuth();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const displayError = localError || error;
  const hasEmailProvider = user?.user.auth_providers?.includes("password") ?? false;
  const sponsorOnly =
    (user?.roles.length ?? 0) > 0 && (user?.roles ?? []).every((r) => r.role_type.startsWith("sponsor"));

  async function handleChangePassword(e: FormEvent) {
    e.preventDefault();
    setLocalError(null);
    setSuccess(false);

    if (newPassword.length < 8) {
      setLocalError("New password must be at least 8 characters");
      return;
    }
    if (newPassword !== confirmPassword) {
      setLocalError("Passwords do not match");
      return;
    }

    setSubmitting(true);
    try {
      await changePassword(currentPassword, newPassword);
      setSuccess(true);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch {
      // error set in auth context
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="mx-auto max-w-lg">
      <h1 className="mb-6 text-2xl font-bold">My Profile</h1>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-base">Account</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3 text-sm">
          {/* 204 -- gap + break-all so a long email cannot collide with its label at 390px */}
          <div className="flex justify-between gap-3">
            <span className="shrink-0 text-muted-foreground">Email</span>
            <span className="break-all text-right font-medium">{user?.user.email}</span>
          </div>
          {user?.user.display_name && (
            <div className="flex justify-between gap-3">
              <span className="shrink-0 text-muted-foreground">Name</span>
              <span className="text-right font-medium">{user.user.display_name}</span>
            </div>
          )}
          {user?.user.phone_number && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">Phone</span>
              <span className="font-medium">{user.user.phone_number}</span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-muted-foreground">Sign-in methods</span>
            <span className="font-medium">
              {user?.user.auth_providers?.map((p) => {
                if (p === "password") return "Email";
                if (p === "google.com") return "Google";
                if (p === "facebook.com") return "Facebook";
                if (p === "phone") return "Phone";
                return p;
              }).join(", ") ?? "--"}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Roles</span>
            <span className="font-medium">
              {user?.roles.length ? user.roles.map((r) => r.role_type.replace("_", " ")).join(", ") : "Spectator"}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* 204 -- child-linking surfaces are parent/spectator concerns; a
          sponsor-only account has no children to link. */}
      {!sponsorOnly && <SchoolAssociationsCard />}
      {!sponsorOnly && <SchoolEmailsCard />}
      {!sponsorOnly && <FamilyCard />}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {hasEmailProvider ? "Change Password" : "Set Password"}
          </CardTitle>
          <CardDescription>
            {hasEmailProvider
              ? "Enter your current password and choose a new one"
              : "Set a password so you can sign in with email and password"
            }
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleChangePassword} className="flex flex-col gap-4">
            {hasEmailProvider && (
              <div className="flex flex-col gap-2">
                <Label htmlFor="current-password">Current Password</Label>
                <Input
                  id="current-password"
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                />
              </div>
            )}
            <div className="flex flex-col gap-2">
              <Label htmlFor="new-password">New Password</Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Minimum 8 characters"
                required
                minLength={8}
                autoComplete="new-password"
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="confirm-password">Confirm New Password</Label>
              <Input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                required
                minLength={8}
                autoComplete="new-password"
              />
            </div>

            {displayError && <p className="text-sm text-destructive">{displayError}</p>}
            {success && <p className="text-sm text-green-600">Password updated successfully</p>}

            <Button type="submit" disabled={submitting}>
              {submitting ? "Saving..." : hasEmailProvider ? "Change Password" : "Set Password"}
            </Button>
          </form>
        </CardContent>
      </Card>

      <PrivacyDataCard />
    </div>
  );
}

function SchoolAssociationsCard() {
  const [associations, setAssociations] = useState<UserSchoolAssociationPublic[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<{ id: string; name: string; display_name: string | null; province: string | null }[]>([]);
  const [searching, setSearching] = useState(false);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.getSchoolAssociations()
      .then((res) => setAssociations(res.associations))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  function refresh() {
    api.getSchoolAssociations().then((res) => setAssociations(res.associations));
  }

  async function searchSchools() {
    if (searchQuery.length < 2) return;
    setSearching(true);
    try {
      const res = await api.searchSchools(searchQuery, { limit: 10 });
      setSearchResults(res.schools.map((s: { id: string; name: string; display_name: string | null; province: string | null }) => ({
        id: s.id, name: s.name, display_name: s.display_name, province: s.province,
      })));
    } catch {
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }

  async function associate(schoolId: string) {
    setAdding(true);
    setError("");
    try {
      await api.createSchoolAssociation({ school_id: schoolId });
      setSearchQuery("");
      setSearchResults([]);
      refresh();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to associate");
    } finally {
      setAdding(false);
    }
  }

  async function leave(schoolId: string) {
    try {
      await api.deleteSchoolAssociation(schoolId);
      refresh();
    } catch {
      // ignore
    }
  }

  return (
    <Card className="mb-6" data-field="school-associations">
      <CardHeader>
        <CardTitle className="text-base">My Schools</CardTitle>
        <CardDescription>Schools you are associated with</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3 text-sm">
        {loading ? (
          <p className="text-muted-foreground">Loading...</p>
        ) : associations.length === 0 ? (
          <p className="text-muted-foreground">Not associated with any school yet.</p>
        ) : (
          associations.map((a) => (
            <div
              key={a.id}
              className="flex items-center justify-between gap-3 border-b pb-2 last:border-0"
              style={a.school_primary_color ? { borderLeft: `3px solid ${a.school_primary_color}`, paddingLeft: 8 } : undefined}
            >
              <div className="flex items-center gap-3 min-w-0">
                {a.school_logo_url ? (
                  <img
                    src={a.school_logo_url}
                    alt=""
                    className="h-8 w-8 shrink-0 rounded-sm object-contain"
                  />
                ) : (
                  <span
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-sm text-[11px] font-bold text-white"
                    style={{ background: a.school_primary_color ?? "var(--color-muted-foreground)" }}
                    aria-hidden="true"
                  >
                    {(a.school_display_name ?? a.school_name).charAt(0).toUpperCase()}
                  </span>
                )}
                <div className="min-w-0">
                  <span className="font-medium">{a.school_display_name ?? a.school_name}</span>
                  {a.linked_children_count > 0 && (
                    <span className="ml-2 text-xs text-muted-foreground">
                      {a.linked_children_count} {a.linked_children_count === 1 ? "child" : "children"}
                    </span>
                  )}
                  {a.status === "pending_verification" && (
                    <span className="ml-2 inline-block bg-yellow-100 text-yellow-800 px-1.5 py-0.5 rounded text-xs">pending</span>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={() => leave(a.school_id)}
                className="text-xs text-muted-foreground hover:text-destructive shrink-0"
              >
                Leave
              </button>
            </div>
          ))
        )}

        {/* Search and add */}
        <div className="mt-2">
          <div className="flex gap-2">
            <Input
              placeholder="Search for a school..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") searchSchools(); }}
              className="text-sm"
            />
            <Button type="button" variant="outline" size="sm" onClick={searchSchools} disabled={searching || searchQuery.length < 2}>
              {searching ? "..." : "Search"}
            </Button>
          </div>
          {searchResults.length > 0 && (
            <div className="mt-2 border rounded-md max-h-48 overflow-y-auto">
              {searchResults.map((s) => {
                const alreadyAssociated = associations.some((a) => a.school_id === s.id);
                return (
                  <div key={s.id} className="flex items-center justify-between px-3 py-2 hover:bg-muted/50 text-sm">
                    <div>
                      <span>{s.display_name ?? s.name}</span>
                      {s.province && <span className="ml-1 text-xs text-muted-foreground">({s.province})</span>}
                    </div>
                    {alreadyAssociated ? (
                      <span className="text-xs text-muted-foreground">Joined</span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => associate(s.id)}
                        disabled={adding}
                        className="text-xs text-primary hover:underline"
                      >
                        Join
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {error && <p className="text-xs text-destructive mt-1">{error}</p>}
        </div>
      </CardContent>
    </Card>
  );
}

function SchoolEmailsCard() {
  const [emails, setEmails] = useState<UserSchoolEmailPublic[]>([]);
  const [loading, setLoading] = useState(true);
  const [newEmail, setNewEmail] = useState("");
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    api.getSchoolEmails()
      .then((res) => setEmails(res.emails))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  function refresh() {
    api.getSchoolEmails().then((res) => setEmails(res.emails));
  }

  async function add() {
    if (!newEmail) return;
    setAdding(true);
    setError("");
    setSuccess("");
    try {
      const res = await api.addSchoolEmail(newEmail);
      setNewEmail("");
      if (res.verification_sent) {
        setSuccess(`Verification email sent to ${res.email.email}. Check your inbox.`);
      } else {
        setSuccess("Email added. We'll send a verification when ready.");
      }
      refresh();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to add email");
    } finally {
      setAdding(false);
    }
  }

  async function remove(emailId: string) {
    try {
      await api.deleteSchoolEmail(emailId);
      refresh();
    } catch {
      // ignore
    }
  }

  return (
    <Card className="mb-6" data-field="school-emails">
      <CardHeader>
        <CardTitle className="text-base">School Email Addresses</CardTitle>
        <CardDescription>
          Add the email your school has on file to link to your children automatically
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3 text-sm">
        {loading ? (
          <p className="text-muted-foreground">Loading...</p>
        ) : emails.length === 0 ? (
          <p className="text-muted-foreground">No school emails added yet.</p>
        ) : (
          emails.map((e) => (
            <div key={e.id} className="flex items-center justify-between border-b pb-2 last:border-0">
              <div>
                <span className="font-mono text-xs">{e.email}</span>
                {e.verified_at ? (
                  <span className="ml-2 inline-block bg-green-100 text-green-800 px-1.5 py-0.5 rounded text-xs">verified</span>
                ) : (
                  <span className="ml-2 inline-block bg-yellow-100 text-yellow-800 px-1.5 py-0.5 rounded text-xs">pending</span>
                )}
                {e.matched_schools > 0 && !e.verified_at && (
                  <span className="ml-1 text-xs text-muted-foreground">
                    ({e.matched_schools} {e.matched_schools === 1 ? "school" : "schools"} matched)
                  </span>
                )}
              </div>
              {!e.verified_at && (
                <button
                  type="button"
                  onClick={() => remove(e.id)}
                  className="text-xs text-muted-foreground hover:text-destructive"
                >
                  Remove
                </button>
              )}
            </div>
          ))
        )}

        {/* Add email */}
        <div className="flex gap-2 mt-2">
          <Input
            type="email"
            placeholder="School email address"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") add(); }}
            className="text-sm"
          />
          <Button type="button" variant="outline" size="sm" onClick={add} disabled={adding || !newEmail}>
            {adding ? "..." : "Add"}
          </Button>
        </div>
        {error && <p className="text-xs text-destructive mt-1">{error}</p>}
        {success && <p className="text-xs text-green-600 mt-1">{success}</p>}
      </CardContent>
    </Card>
  );
}
